// @ts-strict-ignore
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { fromEvent, Observable, ReplaySubject, Subject } from 'rxjs';
import { first } from 'rxjs/operators';

import { noop } from 'src/app/utils/functions/noop';
import { ExecutionMode } from 'src/app/utils/models/execution-mode.enum';

import { ConsentSystemResponse } from './consent-system-response.model';

export const CMP_COOKIE_BANNER_TAGNAME = 'cmm-cookie-banner';

export interface Consent {
  name: string;
  enabled: boolean;
}

export enum UsercentricsEvent {
  ConsentsLoaded = 'usercentrics-consents-loaded',
  ConsentsUpdated = 'usercentrics-consents-updated'
}

/**
 * Service to handle the Usercentrics CMP (Consent Management Platform) / consent.
 * Main difference is the two modes, Standalone and Integrated.
 * For Standalone the CMP needs to be loaded, for Integrated OWCC makes use of OneWeb (but needs to react to the given consent from OneWeb).
 */
@Injectable({
  providedIn: 'root'
})
export class UsercentricsCmpV2Service {
  private readonly subject: ReplaySubject<ConsentSystemResponse> = new ReplaySubject<ConsentSystemResponse>();

  private readonly activateConsentLayerSubject = new Subject<void>();

  public constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  /**
   * @param mode - Standalone or Integrated
   * @param usercentricsSettingsId - id given by usercentrics. Only relevant for the standalone mode.
   * @param abTestingConsentGroupName - e.g. group 'Car Configurator A/B-Testing'. Only relevant for integrated mode (as in standalone we generally have no tracking / AB-testing).
   */
  public init(
    mode: ExecutionMode,
    usercentricsSettingsId: string,
    abTestingConsentGroupName: string
  ): Observable<ConsentSystemResponse> {
    this.registerListeners(abTestingConsentGroupName);

    if (mode === ExecutionMode.Standalone) {
      this.activateConsentLayerSubject.next();
    } else {
      // if we register listeners after they may already been fired by OneWeb, we check for the existence
      // of valid consents values and if there already are, we consider CMP as already initialized
      const cmpCookieBanner = this.document.querySelector(CMP_COOKIE_BANNER_TAGNAME);
      if (cmpCookieBanner) {
        (cmpCookieBanner as Element & { getConsents(): Promise<Consent[]> })
          .getConsents()
          .then(consents => {
            if (consents.length > 0) {
              this.onUsercentricsCmpInitialized(abTestingConsentGroupName, consents);
            }
          })
          .catch(noop);
      }
    }

    return this.subject.asObservable();
  }

  public activateConsentLayer(): Observable<void> {
    return this.activateConsentLayerSubject.asObservable();
  }

  private registerListeners(abTestingConsentGroupName: string): void {
    fromEvent(this.document, UsercentricsEvent.ConsentsLoaded)
      .pipe(first())
      .subscribe((consentsLoadedEvent: CustomEvent<{ consents: Consent[] }>) => {
        // we mark usercentrics as completely initialized when we have the according event _and_ the corresponding consent information
        this.onUsercentricsCmpInitialized(abTestingConsentGroupName, consentsLoadedEvent.detail.consents);
      });

    fromEvent(this.document, UsercentricsEvent.ConsentsUpdated).subscribe(
      (event: CustomEvent<{ consents: Consent[] }>) => {
        this.onConsentChanged(abTestingConsentGroupName, event.detail.consents);
      }
    );
  }

  private onUsercentricsCmpInitialized(abTestingConsentGroupName: string, consents: Consent[]): void {
    this.nextConsentSystemInitializedResponse(abTestingConsentGroupName, consents);
  }

  private onConsentChanged(abTestingConsentGroupName: string, consents: Consent[]): void {
    this.nextConsentSystemInitializedResponse(abTestingConsentGroupName, consents);
  }

  private nextConsentSystemInitializedResponse(abTestingConsentGroupName: string, consents: Consent[] = []): void {
    this.subject.next(
      ConsentSystemResponse.createConsentSystemInitializedResponse(
        this.hasUserGivenConsentForAbTesting(abTestingConsentGroupName, consents)
      )
    );
  }

  private hasUserGivenConsentForAbTesting(abTestingConsentGroupName: string, consents: Consent[] = []): boolean {
    return consents.find(consent => consent.name === abTestingConsentGroupName)?.enabled || false;
  }
}
